/**
 * LandingAnnouncementsTeaser - Phase 86
 *
 * Floating "what's new" teaser that lives in the bottom-right corner
 * of the landing page (Emergent-style). Closed state shows a small
 * stack-of-cards pill with the latest announcement headline; clicking
 * it opens a full-screen modal with:
 *   • Left rail   → vertical scroll of all active announcements
 *   • Right panel → hero image + headline + body + CTA button
 *
 * Reads from the existing public `/api/announcements` feed. The feed
 * now carries optional rich-card fields (Phase 86): `title`, `body`,
 * `image_url`, `cta_text`, `cta_link`, `theme_color`. Legacy text-only
 * announcements still render - they show with `message` as the body
 * and a default gradient.
 *
 * Dismissal is per-user (localStorage keyed on `announcement_id`); a
 * dismissed announcement also hides the floating teaser if it was the
 * latest. Posting a new announcement re-surfaces the teaser.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Resolve an announcement image URL - supports absolute https URLs
 *  (legacy entries + external CDNs) and relative `/api/...` paths
 *  emitted by the new admin-upload endpoint. */
function resolveImageUrl(urlOrPath) {
  if (!urlOrPath) return '';
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  if (urlOrPath.startsWith('/api/')) return `${BACKEND_URL}${urlOrPath}`;
  return urlOrPath;
}

/* Default gradient tones (cycle by index when `theme_color` not set). */
const DEFAULT_GRADIENTS = [
  'linear-gradient(135deg, #FF8E71 0%, #FFB89C 60%, #FFD9C4 100%)',
  'linear-gradient(135deg, #6366F1 0%, #818CF8 60%, #C7D2FE 100%)',
  'linear-gradient(135deg, #10B981 0%, #34D399 60%, #A7F3D0 100%)',
  'linear-gradient(135deg, #F59E0B 0%, #FBBF24 60%, #FDE68A 100%)',
  'linear-gradient(135deg, #EC4899 0%, #F472B6 60%, #FBCFE8 100%)',
];

/* Phase 86e - pool of mascot illustrations. Picked at random per
   sessionStorage key so returning visitors see fresh personality on
   most visits, but the choice is stable within a single session. */
const MASCOTS = ['monkey', 'elephant', 'lion', 'owl'];

function chosenMascot() {
  try {
    const stored = sessionStorage.getItem('byb_mascot');
    if (stored && MASCOTS.includes(stored)) return stored;
    const pick = MASCOTS[Math.floor(Math.random() * MASCOTS.length)];
    sessionStorage.setItem('byb_mascot', pick);
    return pick;
  } catch {
    return MASCOTS[0];
  }
}

function gradientFor(item, idx) {
  if (item.theme_color) {
    // Single solid → soft 2-stop gradient for visual depth.
    return `linear-gradient(135deg, ${item.theme_color} 0%, ${item.theme_color}AA 100%)`;
  }
  return DEFAULT_GRADIENTS[idx % DEFAULT_GRADIENTS.length];
}

export default function LandingAnnouncementsTeaser() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [teaserIdx, setTeaserIdx] = useState(0);   // which card is shown in the floating teaser right now
  const [teaserPaused, setTeaserPaused] = useState(false); // pauses auto-cycle while hovered
  const [mascot] = useState(chosenMascot);
  const [hidden, setHidden] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('byb_dismissed_announcements') || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/announcements`)
      .then(({ data }) => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  /* Lock body scroll while the modal is open. */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /* Esc closes. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const visible = useMemo(
    () => items.filter((a) => !hidden[a.announcement_id]),
    [items, hidden]
  );

  /* Phase 86h - auto-cycle the floating teaser through every active
     announcement, Emergent-style. Pauses while the user hovers and
     while the full modal is open. Snaps back to index 0 if the
     visible list shrinks below the current pointer. */
  useEffect(() => {
    if (visible.length <= 1) return;
    if (open || teaserPaused) return;
    const id = setInterval(() => {
      setTeaserIdx((i) => (i + 1) % visible.length);
    }, 5500);
    return () => clearInterval(id);
  }, [visible.length, open, teaserPaused]);

  /* Keep teaserIdx in range if announcements get dismissed/deleted. */
  useEffect(() => {
    if (teaserIdx >= visible.length) setTeaserIdx(0);
  }, [visible.length, teaserIdx]);

  const dismissAll = () => {
    const next = { ...hidden };
    items.forEach((a) => { next[a.announcement_id] = true; });
    setHidden(next);
    try {
      localStorage.setItem('byb_dismissed_announcements', JSON.stringify(next));
    } catch { /* quota: ignore */ }
    setOpen(false);
  };

  if (visible.length === 0) return null;

  /* Phase 86h - teaser auto-cycles, so derive from the live index.
     Fall back to first visible item if index drifts out of range. */
  const teaserIndexSafe = teaserIdx >= visible.length ? 0 : teaserIdx;
  const teaser = visible[teaserIndexSafe];
  const active = visible[activeIdx] || teaser;
  const headline = (item) => item.title || item.message?.slice(0, 60) || 'Latest update';

  return (
    <>
      {/* Phase 86d - keyframes for the monkey image + sparkles + float. */}
      <style>{`
        @keyframes byb-teaser-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes byb-monkey-bob {
          /* IMPORTANT: must include translateX(-50%) here because this
             rule replaces the Tailwind -translate-x-1/2 utility on
             the same transform property. Without it the monkey
             jumps to the right of the card. */
          0%, 100% { transform: translateX(-50%) rotate(-1.5deg) translateY(0); }
          50%      { transform: translateX(-50%) rotate(1.5deg)  translateY(-2px); }
        }
        @keyframes byb-twinkle-a {
          0%, 100% { opacity: 0.2; transform: scale(0.7); }
          50%      { opacity: 1;   transform: scale(1.1); }
        }
        @keyframes byb-twinkle-b {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.3; transform: scale(0.6); }
        }
        @keyframes byb-glow-pulse {
          0%, 100% { box-shadow: 0 14px 36px -10px rgba(255,142,113,0.55), 0 0 0 1px rgba(255,255,255,0.45) inset; }
          50%      { box-shadow: 0 20px 50px -8px rgba(255,142,113,0.75),  0 0 0 1px rgba(255,255,255,0.55) inset; }
        }
        @keyframes byb-card-swap {
          /* Soft cross-fade + tiny rise when the teaser switches to
             the next announcement. The key prop on the swap node
             remounts the element, so this animation plays once on
             every change of teaserIdx. */
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .byb-teaser-wrap   { animation: byb-teaser-float 4s ease-in-out infinite; }
        .byb-teaser-card   { animation: byb-glow-pulse 3.4s ease-in-out infinite; transition: transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1); }
        .byb-teaser-swap   { animation: byb-card-swap 380ms cubic-bezier(0.4, 0, 0.2, 1) both; }
        .byb-monkey-img    { animation: byb-monkey-bob 3.6s ease-in-out infinite; transform-origin: 50% 70%; transition: transform .25s; }
        .byb-sparkle-a     { animation: byb-twinkle-a 2.2s ease-in-out infinite; }
        .byb-sparkle-b     { animation: byb-twinkle-b 1.8s ease-in-out infinite 0.4s; }
        .byb-sparkle-c     { animation: byb-twinkle-a 2.6s ease-in-out infinite 0.9s; }
        /* Hover: card lifts, monkey gets a slightly bigger excited pose. */
        .byb-teaser-group:hover .byb-teaser-card { transform: translateY(-4px); }
        .byb-teaser-group:hover .byb-monkey-img  { animation-play-state: paused; transform: translateX(-50%) rotate(0deg) translateY(-4px) scale(1.05); }
        @media (prefers-reduced-motion: reduce) {
          .byb-teaser-wrap, .byb-teaser-card, .byb-teaser-swap, .byb-monkey-img,
          .byb-sparkle-a, .byb-sparkle-b, .byb-sparkle-c { animation: none !important; }
        }
      `}</style>

      {/* ── Floating teaser ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => { setActiveIdx(teaserIndexSafe); setOpen(true); }}
        onMouseEnter={() => setTeaserPaused(true)}
        onMouseLeave={() => setTeaserPaused(false)}
        onFocus={() => setTeaserPaused(true)}
        onBlur={() => setTeaserPaused(false)}
        data-testid="landing-announcements-teaser"
        aria-label={`Open announcements - showing ${teaserIndexSafe + 1} of ${visible.length}: ${headline(teaser)}`}
        className="byb-teaser-group fixed z-40 right-4 sm:right-6 group"
        style={{
          /* Stack from the viewport bottom upward, in this order:
                Made with Emergent (≈ 16px + 38px = 54px tall)  → 0 to 54px
                Monkey legs/feet                                  → 60–~100px
                Monkey body/head (visible below card)             → ~100–~230px
                Card bottom                                       → 240px
                Card top                                          → ~340px
             That leaves a comfortable ~6px gap between the
             mascot's feet and the "Made with Emergent" badge. */
          bottom: 'max(240px, env(safe-area-inset-bottom, 16px) + 226px)',
          width: 'min(86vw, 280px)',
        }}
      >
        <span className="byb-teaser-wrap block relative">
          {/* Twinkle sparkles around the card */}
          <svg
            aria-hidden="true"
            className="absolute -top-3 -left-3 byb-sparkle-a"
            width="22" height="22" viewBox="0 0 24 24" fill="none"
          >
            <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" fill="#FFB347" />
          </svg>
          <svg
            aria-hidden="true"
            className="absolute -top-1 right-4 byb-sparkle-b"
            width="14" height="14" viewBox="0 0 24 24" fill="none"
          >
            <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" fill="#FF6B9D" />
          </svg>
          <svg
            aria-hidden="true"
            className="absolute top-6 -right-3 byb-sparkle-c"
            width="16" height="16" viewBox="0 0 24 24" fill="none"
          >
            <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" fill="#7DD3FC" />
          </svg>

          {/* Cartoon mascot holding the card. Phase 119.14 - WebP
              variant ships at ~21-38 KB (was 150 KB PNG) and the explicit
              width/height attrs eliminate the layout-shift culprit
              Lighthouse flagged. PNG kept as a defensive <source> sibling
              for older browsers that don't speak WebP. */}
          <picture>
            <source
              type="image/webp"
              srcSet={`/api/assets/mascots/${mascot}-mascot.webp`}
            />
            <img
              src={`/api/assets/mascots/${mascot}-mascot.png`}
              alt={`Build Your Book ${mascot} mascot peeking from the announcements card on buildyourbook.in`}
              className="byb-monkey-img absolute left-1/2 -translate-x-1/2 select-none"
              draggable={false}
              loading="lazy"
              decoding="async"
              width="200"
              height="200"
              style={{
                /* Image height ≈ 65% of the 280px card width = ~182px.
                   bottom = -(image_height - desired_overlap)
                          = -(182 - 18) = -164. */
                bottom: '-164px',
                width: '65%',
                maxWidth: 200,
                height: 'auto',
                filter: 'drop-shadow(0 8px 12px rgba(45,55,72,0.22))',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          </picture>

          {/* Announcement card */}
          <span
            className="byb-teaser-card relative block rounded-2xl text-left p-[2px]"
            style={{ background: gradientFor(teaser, 0) }}
          >
            <span
              className="block rounded-[14px] px-4 py-3"
              style={{ background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)' }}
            >
              <span className="flex items-center gap-2 mb-1">
                <Sparkles size={14} style={{ color: '#FF8E71' }} />
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.14em]"
                  style={{ color: '#9A3412' }}
                >
                  What&apos;s new
                  {visible.length > 1 && (
                    <span
                      className="ml-1.5 px-1.5 rounded-full text-[9px]"
                      style={{ background: '#C2410C', color: 'white' }}
                    >
                      {visible.length}
                    </span>
                  )}
                </span>
              </span>
              {/* The headline + helper line re-mount on every cycle so
                  the `byb-teaser-swap` keyframe animates the fade.
                  `key` is the announcement_id (not the index) so
                  React doesn't accidentally reuse the DOM node when
                  the same item resurfaces. */}
              <span
                key={teaser.announcement_id}
                className="byb-teaser-swap block"
                aria-live="polite"
              >
                <span
                  className="block text-sm font-semibold leading-snug line-clamp-2"
                  style={{ color: '#2D3748', fontFamily: 'Fredoka' }}
                >
                  {headline(teaser)}
                </span>
                <span
                  className="block text-[10px] mt-1 font-medium"
                  style={{ color: '#9A3412' }}
                >
                  Tap to peek inside →
                </span>
              </span>
              {/* Tiny progress dots - only when there's more than one
                  announcement. Active dot is wider + brand-coloured. */}
              {visible.length > 1 && (
                <span
                  className="flex items-center gap-1 mt-2"
                  aria-hidden="true"
                  data-testid="landing-announcements-dots"
                >
                  {visible.map((it, i) => (
                    <span
                      key={it.announcement_id}
                      style={{
                        display: 'inline-block',
                        height: 4,
                        width: i === teaserIndexSafe ? 16 : 5,
                        borderRadius: 999,
                        background: i === teaserIndexSafe ? '#FF8E71' : 'rgba(154,52,18,0.25)',
                        transition: 'width 360ms ease, background 360ms ease',
                      }}
                    />
                  ))}
                </span>
              )}
            </span>
          </span>
        </span>
      </button>

      {/* ── Full-screen modal - Emergent-style large layout ─── */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ background: 'rgba(15, 23, 42, 0.78)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="landing-announcements-modal"
        >
          <div
            className="relative w-full max-w-[min(1400px,96vw)] h-[88vh] max-h-[88vh] rounded-[28px] overflow-hidden flex flex-col sm:flex-row shadow-2xl"
            style={{ background: '#0F172A', color: 'white' }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="landing-announcements-close"
              aria-label="Close announcements"
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.12)', color: 'white' }}
            >
              <X size={20} />
            </button>

            {/* Left rail - stack of thumbnails */}
            <aside
              className="w-full sm:w-[300px] flex-shrink-0 border-b sm:border-b-0 sm:border-r"
              style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <div className="px-6 pt-6 pb-3">
                <p
                  className="text-sm font-semibold leading-snug"
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                >
                  Exclusive updates
                  <span className="ml-1" aria-hidden="true">✨</span>
                </p>
              </div>
              <div
                className="px-4 pb-4 flex sm:flex-col gap-3 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto"
                style={{ maxHeight: 'calc(88vh - 64px)' }}
                data-testid="landing-announcements-rail"
              >
                {visible.map((item, idx) => {
                  const isActive = idx === activeIdx;
                  const thumbSrc = resolveImageUrl(item.image_url);
                  return (
                    <button
                      key={item.announcement_id}
                      type="button"
                      onClick={() => setActiveIdx(idx)}
                      data-testid={`landing-announcement-rail-${item.announcement_id}`}
                      className="relative rounded-2xl overflow-hidden flex-shrink-0 sm:flex-shrink-1 text-left transition"
                      style={{
                        width: 'clamp(180px, 24vw, 268px)',
                        height: 150,
                        outline: isActive ? '3px solid #FF8E71' : '2px solid transparent',
                        outlineOffset: isActive ? '-3px' : '0',
                      }}
                      aria-pressed={isActive}
                      aria-label={`Show: ${headline(item)}`}
                    >
                      {thumbSrc ? (
                        <img
                          src={thumbSrc}
                          alt=""
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="absolute inset-0"
                          style={{ background: gradientFor(item, idx) }}
                        />
                      )}
                      <span
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.65) 100%)' }}
                      />
                      <span className="absolute inset-x-0 bottom-0 p-3">
                        <span
                          className="block rounded-lg px-2.5 py-1.5 text-xs font-bold leading-tight line-clamp-2"
                          style={{ background: 'rgba(255,255,255,0.94)', color: '#1F2937' }}
                        >
                          {headline(item)}
                        </span>
                      </span>
                    </button>
                  );
                })}

                {visible.length > 1 && (
                  <button
                    type="button"
                    onClick={dismissAll}
                    data-testid="landing-announcements-dismiss-all"
                    className="mt-3 text-xs font-medium hover:underline text-left px-2"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    Dismiss all
                  </button>
                )}
              </div>
            </aside>

            {/* Right detail panel */}
            <section
              className="flex-1 flex flex-col min-h-0"
              data-testid="landing-announcement-detail"
            >
              {/* Hero - tall and dramatic */}
              <div
                className="relative w-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{
                  height: '58%',
                  minHeight: 280,
                  background: gradientFor(active, activeIdx),
                }}
              >
                {active.image_url && (
                  <img
                    src={resolveImageUrl(active.image_url)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <span
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background: active.image_url
                      ? 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.5) 100%)'
                      : 'transparent',
                  }}
                />
                {!active.image_url && (
                  <h2
                    className="relative px-12 text-center font-bold text-white"
                    style={{
                      fontFamily: 'Fredoka',
                      fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                      textShadow: '0 4px 32px rgba(0,0,0,0.4)',
                      lineHeight: 1.05,
                    }}
                  >
                    {headline(active)}
                  </h2>
                )}
              </div>

              {/* Body - Emergent-style white card with bold title + CTA */}
              <div
                className="flex-1 px-8 sm:px-14 py-8 sm:py-10 overflow-y-auto min-h-0"
                style={{ background: '#FFFFFF', color: '#1F2937' }}
              >
                <h3
                  className="font-bold mb-4"
                  style={{
                    fontFamily: 'Fredoka',
                    fontSize: 'clamp(1.75rem, 2.8vw, 2.5rem)',
                    color: '#0F172A',
                    lineHeight: 1.15,
                  }}
                >
                  {active.title || headline(active)}
                </h3>
                <p
                  className="text-base sm:text-lg leading-relaxed mb-8 max-w-3xl"
                  style={{ color: '#4A5568' }}
                >
                  {active.body || active.message}
                </p>

                {active.cta_link && (
                  <a
                    href={active.cta_link}
                    target={active.cta_link.startsWith('http') ? '_blank' : undefined}
                    rel={active.cta_link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    data-testid="landing-announcement-cta"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-base transition hover:scale-[1.02]"
                    style={{ background: '#0F172A', color: 'white' }}
                  >
                    {active.cta_text || 'Learn more'}
                    <ArrowRight size={18} />
                  </a>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </>
  );
}
